import React from 'react';
import { Form, Input, Button, Row, Col, Select, Space, Divider, Typography, Avatar, Upload, message, Grid } from 'antd';
import {
  UserOutlined,
  HomeOutlined,
  CreditCardOutlined,
  EnvironmentOutlined,
  MobileOutlined,
  MailOutlined,
  UploadOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  DeleteOutlined,
  PushpinOutlined,
  PlusOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import { customerService } from '../../services/customerService';
import { notification } from 'antd';
import api from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const CustomerForm = ({ customer, prefill, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [aadharFiles, setAadharFiles] = React.useState([]);
  const [panFile, setPanFile] = React.useState(null);
  const [aadharFileList, setAadharFileList] = React.useState([]);
  const [panFileList, setPanFileList] = React.useState([]);

  React.useEffect(() => {
    if (customer) {
      // Normalize consumer numbers: fallback to consumerNo if consumerNumbers is empty
      const initialConsumerNumbers = (customer.consumerNumbers && customer.consumerNumbers.length > 0)
        ? customer.consumerNumbers
        : (customer.consumerNo ? [{ consumerNumber: customer.consumerNo }] : [{ consumerNumber: '' }]);

      form.setFieldsValue({
        ...customer,
        consumerNumbers: initialConsumerNumbers
      });

      // Set existing files if available
      if (customer.aadharFilePath) {
        let paths = [];
        try {
          if (customer.aadharFilePath.startsWith('[')) {
            paths = JSON.parse(customer.aadharFilePath);
          } else {
            paths = [customer.aadharFilePath];
          }
        } catch (e) {
          paths = [customer.aadharFilePath];
        }
        
        const baseURL = api.defaults.baseURL.replace(/\/api\/?$/, "");
        
        setAadharFileList(paths.map((p, i) => {
          const fileUrl = p.startsWith('http') ? p : `${baseURL}/api/${p.replace(/^\/+/, "")}`;
          return {
            uid: `${-1 - i}`,
            name: `Aadhar Card ${paths.length > 1 ? i + 1 : ''}`.trim(),
            status: 'done',
            url: fileUrl,
            thumbUrl: fileUrl,
          };
        }));
      }

      if (customer.panFilePath) {
        let paths = [];
        try {
          if (customer.panFilePath.startsWith('[')) {
            paths = JSON.parse(customer.panFilePath);
          } else {
            paths = [customer.panFilePath];
          }
        } catch (e) {
          paths = [customer.panFilePath];
        }

        const baseURL = api.defaults.baseURL.replace(/\/api\/?$/, "");
        setPanFileList(paths.map((p, i) => {
          const fileUrl = p.startsWith('http') ? p : `${baseURL}/api/${p.replace(/^\/+/, "")}`;
          return {
            uid: `pan-${-1 - i}`,
            name: `PAN Card ${paths.length > 1 ? i + 1 : ''}`.trim(),
            status: 'done',
            url: fileUrl,
            thumbUrl: fileUrl,
          };
        }));
      }
    } else {
      form.resetFields();
      setAadharFiles([]);
      setAadharFileList([]);
      setPanFileList([]);

      if (prefill && typeof prefill === 'object') {
        form.setFieldsValue({
          ...prefill,
          consumerNumbers: prefill.consumerNumbers || [{ consumerNumber: '' }]
        });
      } else {
        form.setFieldsValue({
          consumerNumbers: [{ consumerNumber: '' }]
        });
      }
    }
  }, [customer, prefill, form]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Clean empty strings to null or omit to avoid DB unique constraint issues
      const data = { ...values };
      Object.keys(data).forEach(key => {
        if (data[key] === '') {
          data[key] = null;
        }
      });

      let customerId;
      if (customer) {
        await customerService.updateCustomer(customer.id, data);
        customerId = customer.id;
        message.success('Customer updated successfully');
      } else {
        const response = await customerService.createCustomer(data);
        customerId = response.data.id;
        message.success('Customer created successfully');
      }

      // Upload files if provided
      try {
        if (aadharFiles.length > 0) {
          for (const file of aadharFiles) {
            await customerService.uploadAadhar(customerId, file);
          }
          message.success('Aadhar card uploaded successfully');
        }

        if (panFile) {
          await customerService.uploadPan(customerId, panFile);
          message.success('PAN card uploaded successfully');
        }
      } catch (fileError) {
        // Detailed error reporting from API
        const errorMsg = fileError.error || fileError.message || JSON.stringify(fileError);
        message.warning('Customer saved but file upload failed: ' + errorMsg);
        console.error('Document upload error:', fileError);
      }

      onSuccess();
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to save customer'
      });
    } finally {
      setLoading(false);
    }
  };

  // File upload handlers
  const handleAadharChange = ({ fileList }) => {
    setAadharFileList(fileList);
    const files = fileList
      .filter(f => f.originFileObj)
      .map(f => f.originFileObj);
    setAadharFiles(files);
  };

  const handlePanChange = ({ file, fileList }) => {
    setPanFileList(fileList);

    if (file.status !== 'uploading') {
      setPanFile(file.originFileObj || file);
    }
  };

  const beforeUpload = (file) => {
    const isValidType = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'application/pdf';
    if (!isValidType) {
      message.error('You can only upload JPG, PNG, or PDF files!');
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('File must be smaller than 5MB!');
      return Upload.LIST_IGNORE;
    }
    return false; // Prevent auto upload
  };

  const handlePincodeChange = async (e) => {
    const pincode = e.target.value;
    if (pincode && pincode.length === 6 && /^[0-9]{6}$/.test(pincode)) {
      try {
        const API_URL = import.meta.env.VITE_API_URL || '/api';
        const response = await fetch(`${API_URL}/pincode/${pincode}`);
        const data = await response.json();

        if (data && data.success) {
          form.setFieldsValue({
            district: data.district,
            subDistrict: data.subDistrict,
          });
          message.success(`District & Sub-district filled for pincode ${pincode}`);
        } else {
          message.warning('No location data found for this pincode. Please fill manually.');
        }
      } catch (error) {
        console.error('Error fetching pincode details:', error);
        message.error('Could not fetch location for this pincode. Please fill district & sub-district manually.');
      }
    }
  };

  const handleDeleteAadhar = async () => {
    if (customer && customer.id) {
      try {
        await customerService.deleteCustomerFile(customer.id, 'aadhar');
        setAadharFileList([]);
        setAadharFiles([]);
        message.success('Aadhar card deleted successfully');
      } catch (error) {
        message.error('Failed to delete Aadhar card');
      }
    } else {
      setAadharFileList([]);
      setAadharFiles([]);
    }
  };

  const handleDeletePan = async () => {
    if (customer && customer.id) {
      try {
        await customerService.deleteCustomerFile(customer.id, 'pan');
        setPanFileList([]);
        setPanFile(null);
        message.success('PAN card deleted successfully');
      } catch (error) {
        message.error('Failed to delete PAN card');
      }
    } else {
      setPanFileList([]);
      setPanFile(null);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      autoComplete="off"
      initialValues={{
        locationType: 'Urban',
        houseOwnership: 'Single',
        customerAddress: ''
      }}
      style={{ padding: '0 10px', paddingBottom: isMobile ? '80px' : '40px' }}
    >
      <div style={{ marginBottom: 24 }}>
        <Space size="middle">
          <Avatar size="large" icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {customer ? 'Edit Customer Details' : 'Add New Customer'}
            </Title>
            <Text type="secondary">All fields are mandatory. Please provide accurate information.</Text>
          </div>
        </Space>
      </div>

      <Divider orientation="left"><Space><UserOutlined />Basic Information</Space></Divider>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Customer Name"
            name="customerName"
            rules={[
              { required: true, message: 'Please enter customer name' },
              { pattern: /^[^0-9]*$/, message: 'Numbers are not allowed in name' }
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="Full Name" size="large" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Consumer Numbers</div>
          <Form.List
            name="consumerNumbers"
            initialValue={[{ consumerNumber: '' }]}
          >
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, 'consumerNumber']}
                      rules={[
                        { required: true, message: 'Missing consumer number' },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const allNumbers = form.getFieldValue('consumerNumbers') || [];
                            const duplicates = allNumbers.filter(
                              (item) => item?.consumerNumber === value
                            );
                            if (duplicates.length > 1) {
                              return Promise.reject(new Error('Duplicate consumer number'));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input prefix={<CreditCardOutlined />} placeholder="Consumer No." size={isMobile ? 'middle' : 'large'} style={{ width: isMobile ? '100%' : 180 }} />
                    </Form.Item>
                    {fields.length > 1 && (
                      <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                    )}
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} size="small">
                    Add another consumer number
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Mobile Number"
            name="customerMobile"
            rules={[
              { required: true, message: 'Please enter mobile number' },
              { pattern: /^\d{10}$/, message: 'Please enter a valid 10-digit number' }
            ]}
          >
            <Input
              prefix={<MobileOutlined />}
              placeholder="10-digit mobile number"
              size="large"
              maxLength={10}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Email Address"
            name="customerEmail"
            rules={[
              { required: true, message: 'Please enter email address' },
              { type: 'email', message: 'Please enter valid email' }
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="email@example.com" size="large" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            label="Occupation"
            name="occupation"
            rules={[{ required: true, message: 'Please enter occupation' }]}
          >
            <Input placeholder="Current occupation" size="large" />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left"><Space><EnvironmentOutlined />Address & Location</Space></Divider>
      <Row gutter={[16, 0]}>
        <Col span={24}>
          <Form.Item
            label="Full Address"
            name="customerAddress"
            rules={[{ required: true, message: 'Please enter full address' }]}
          >
            <TextArea rows={3} placeholder="Apartment, Street, Area" size="large" autoComplete="off" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            label="District"
            name="district"
            rules={[
              { required: true, message: 'Please enter district' },
              { pattern: /^[^0-9]*$/, message: 'Numbers are not allowed' }
            ]}
          >
            <Input placeholder="District" size="large" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            label="Sub-district / Taluka"
            name="subDistrict"
            rules={[
              { required: true, message: 'Please enter sub-district/taluka' },
              { pattern: /^[^0-9]*$/, message: 'Numbers are not allowed' }
            ]}
          >
            <Input placeholder="Taluka" size="large" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            label="Pincode"
            name="pincode"
            rules={[
              { required: true, message: 'Please enter pincode' },
              { pattern: /^\d{6}$/, message: 'Pincode must be 6 digits' }
            ]}
          >
            <Input
              prefix={<PushpinOutlined />}
              placeholder="6-digit pincode"
              size="large"
              maxLength={6}
              onChange={handlePincodeChange}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            label="Location Type"
            name="locationType"
            rules={[{ required: true, message: 'Please select location type' }]}
          >
            <Select size="large">
              <Option value="Urban">Urban</Option>
              <Option value="Rural">Rural</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left"><Space><CreditCardOutlined />KYC & Tax Details</Space></Divider>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="GSTN"
            name="gstn"
            rules={[
              { pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, message: 'Invalid GST Format' }
            ]}
            normalize={(value) => value?.toUpperCase()}
          >
            <Input placeholder="GST Identification Number" size="large" maxLength={15} style={{ textTransform: 'uppercase' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Aadhaar Number"
            name="aadharNo"
            rules={[
              { required: true, message: 'Please enter Aadhaar number' },
              { pattern: /^\d{12}$/, message: 'Aadhaar must be 12 digits' }
            ]}
          >
            <Input
              placeholder="12-digit Aadhaar"
              size="large"
              maxLength={12}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="PAN Number"
            name="panNo"
            rules={[
              { pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, message: 'Invalid PAN format (e.g., ABCDE1234F)' }
            ]}
            normalize={(value) => value?.toUpperCase()}
          >
            <Input placeholder="ABCDE1234F (Optional)" size="large" maxLength={10} style={{ textTransform: 'uppercase' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="House Ownership"
            name="houseOwnership"
            rules={[{ required: true, message: 'Please select house ownership type' }]}
          >
            <Select size="large">
              <Option value="Single">Single Ownership</Option>
              <Option value="Joint">Joint Ownership</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left"><Space><UploadOutlined />Document Upload</Space></Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Aadhaar Card"
            required
            rules={[{ required: true, message: 'Please upload Aadhaar card' }]}
          >
            <Upload
              fileList={aadharFileList}
              onChange={handleAadharChange}
              beforeUpload={beforeUpload}
              maxCount={2}
              accept=".jpg,.jpeg,.png,.pdf"
              listType="picture"
              multiple
            >
              {aadharFileList.length < 2 && (
                <Button icon={<UploadOutlined />} size="large" block>
                  Upload Aadhaar Card ({aadharFileList.length}/2)
                </Button>
              )}
            </Upload>
            {aadharFileList.length > 0 && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteAadhar}
                style={{ marginTop: 8 }}
                size="small"
              >
                Remove Aadhaar
              </Button>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="PAN Card (Optional)"
          >
            <Upload
              fileList={panFileList}
              onChange={handlePanChange}
              beforeUpload={beforeUpload}
              maxCount={1}
              accept=".jpg,.jpeg,.png,.pdf"
              listType="picture"
            >
              <Button icon={<UploadOutlined />} size="large" block>
                Upload PAN Card
              </Button>
            </Upload>
            {panFileList.length > 0 && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeletePan}
                style={{ marginTop: 8 }}
                size="small"
              >
                Remove PAN
              </Button>
            )}
          </Form.Item>
        </Col>
      </Row>

      <div style={{
        position: 'absolute',
        right: 0,
        bottom: 0,
        width: '100%',
        borderTop: '1px solid #e9e9e9',
        padding: '10px 16px',
        background: '#fff',
        textAlign: 'left',
        zIndex: 1,
      }}>
        <Space>
          <Button onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading} size="large">
            {customer ? 'Update' : 'Create'} Customer
          </Button>
        </Space>
      </div>
    </Form>
  );
};

export default CustomerForm;
